import React, { useState, useEffect } from 'react';
import { ArrowRight, Building, Search, Edit, Save, Plus, Trash2, DollarSign, RefreshCw, X, FileText, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Categories = () => {
  // الحالات (States)
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    CompanyID: '',
    PaymentAmount: '',
    Notes: '',
    EffectiveFrom: new Date().toISOString().slice(0, 10) // تاريخ اليوم بتنسيق YYYY-MM-DD
  });
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  // جلب بيانات الشركات عند تحميل الصفحة
  useEffect(() => {
    fetchCompanies();
  }, []);

  // دالة جلب بيانات الشركات
  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/companies');
      if (!response.ok) throw new Error('فشل في جلب بيانات الشركات');
      
      const data = await response.json();
      
      // إضافة حقل PaymentAmount إذا لم يكن موجودا
      const companiesWithPayments = data.map(company => ({
        ...company,
        PaymentAmount: company.PaymentAmount || 0,
        Notes: company.Notes || ''
      }));
      
      setCompanies(companiesWithPayments);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في جلب بيانات الشركات:', error);
      showToast('فشل في جلب بيانات الشركات', 'error');
      setLoading(false);
    }
  };

  // دالة حفظ مبلغ الدفع للشركة
// دالة حفظ مبلغ الدفع للشركة
const saveCompanyPayment = async () => {
  if (!formData.CompanyID) {
    showToast('يرجى اختيار شركة', 'error');
    return;
  }
  
  if (!formData.PaymentAmount) {
    showToast('يرجى إدخال مبلغ الدفع', 'error');
    return;
  }
  
  if (!formData.EffectiveFrom) {
    showToast('يرجى تحديد تاريخ بداية السريان', 'error');
    return;
  }

  setLoading(true);
  try {
    // تحديث بيانات الدفع للشركة المحددة
    const response = await fetch(`/api/companies/${formData.CompanyID}/payment`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        PaymentAmount: parseFloat(formData.PaymentAmount),
        Notes: formData.Notes,
        EffectiveFrom: formData.EffectiveFrom,
        UpdatedBy: 'Admin' // يمكن تغييره حسب نظام المستخدمين لديك
      }),
    });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'فشل في تحديث بيانات الدفع');
      }

      // تحديث البيانات محليًا
// تحديث البيانات محليًا
setCompanies(prevCompanies => 
    prevCompanies.map(company => 
      parseInt(company.CompanyID) === parseInt(formData.CompanyID) 
        ? { 
            ...company, 
            PaymentAmount: parseFloat(formData.PaymentAmount), 
            Notes: formData.Notes 
          } 
        : company
    )
  );

      resetForm();
      showToast('تم تحديث بيانات الدفع بنجاح', 'success');
    } catch (error) {
      console.error('خطأ في تحديث بيانات الدفع:', error);
      showToast(error.message || 'فشل في تحديث بيانات الدفع', 'error');
    } finally {
      setLoading(false);
    }
};
  // دالة إظهار التنبيه (Toast)
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  // دالة تحديد الشركة للتعديل
  const handleEdit = (company) => {
    setFormData({
      CompanyID: company.CompanyID,
      PaymentAmount: company.PaymentAmount || '',
      Notes: company.Notes || ''
    });
    setEditingId(company.CompanyID);
    setShowForm(true);
  };

  // دالة إعادة تعيين النموذج
  const resetForm = () => {
    setFormData({
      CompanyID: '',
      PaymentAmount: '',
      Notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };


  // دالة لعرض تاريخ إصدارات مبالغ الدفع
  const showVersionHistory = async (companyId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/companies/${companyId}/payment-versions`);
      if (!response.ok) throw new Error('فشل في جلب تاريخ إصدارات مبالغ الدفع');
      
      const data = await response.json();
      setVersionHistory(data);
      
      // تحديد اسم الشركة
      const companyName = companies.find(c => c.CompanyID === companyId)?.CompanyName || '';
      setSelectedCompanyName(companyName);
      
      setShowVersionModal(true);
    } catch (error) {
      console.error('خطأ في جلب تاريخ إصدارات مبالغ الدفع:', error);
      showToast('فشل في جلب تاريخ إصدارات مبالغ الدفع', 'error');
    } finally {
      setLoading(false);
    }
  };


  // دالة تصدير البيانات إلى Excel
  const exportToExcel = async () => {
    showToast('جاري تصدير البيانات...', 'info');
    try {
      const response = await fetch('/api/companies/export/payments');
      if (!response.ok) throw new Error('فشل في جلب بيانات التصدير');
      
      const data = await response.json();
      
      // اعداد ملف Excel للتنزيل
      const XLSX = window.XLSX; // تأكد من تضمين مكتبة XLSX في المشروع
      if (!XLSX) {
        throw new Error('مكتبة XLSX غير متوفرة');
      }
      
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "مبالغ الدفع");
      XLSX.writeFile(wb, `مبالغ_الدفع_${new Date().toISOString().slice(0, 10)}.xlsx`);
      
      showToast('تم تصدير البيانات بنجاح', 'success');
    } catch (error) {
      console.error('خطأ في تصدير البيانات:', error);
      showToast('فشل في تصدير البيانات: ' + error.message, 'error');
    }
};

  // تصفية الشركات حسب البحث
  const filteredCompanies = companies.filter(company =>
    company.CompanyName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-6" dir="rtl">
      <div className="max-w-7xl mx-auto px-4">
        {/* شريط التنقل */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700 transition-colors"
            >
              <ArrowRight size={20} />
              العودة للرئيسية
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building size={24} className="text-blue-600" />
            إدارة تصنيفات الشركات ومبالغ الدفع
          </h1>
        </div>

        {/* شريط الأدوات */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
              >
                <Plus size={18} />
                تحديد مبلغ دفع
              </button>

              <button
                onClick={fetchCompanies}
                className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors"
              >
                <RefreshCw size={18} />
                تحديث البيانات
              </button>

              <button
                onClick={exportToExcel}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 transition-colors"
              >
                <Download size={18} />
                تصدير Excel
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="البحث عن شركة..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-64 rounded-md border-0 py-2 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        {/* بطاقات الشركات */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {loading ? (
            <div className="col-span-3 text-center py-12">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">جاري تحميل البيانات...</p>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="col-span-3 bg-white rounded-xl shadow-md p-8 text-center">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">لا توجد بيانات متاحة للشركات</p>
            </div>
          ) : (
            filteredCompanies.map(company => (
              <div 
                key={company.CompanyID} 
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-5 border-b border-gray-100">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-lg text-gray-900">{company.CompanyName}</h3>
                    <button 
                      onClick={() => handleEdit(company)}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="تعديل مبلغ الدفع"
                    >
                      <Edit size={18} />
                    </button>
                  </div>
                  {company.PhoneNumber && (
                    <p className="text-sm text-gray-500 mt-1">{company.PhoneNumber}</p>
                  )}
                </div>
                  <div className="p-5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">مبلغ الدفع الحالي:</span>
                    <span className="font-bold text-lg text-blue-600">
                      {parseFloat(company.PaymentAmount).toLocaleString()} ريال
                    </span>
                  </div>
                  
                  <button 
                    onClick={() => showVersionHistory(company.CompanyID)}
                    className="w-full mt-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 rounded-lg py-1 px-3 flex items-center justify-center gap-1 hover:bg-blue-50 transition-colors"
                  >
                    <FileText size={14} />
                    عرض تاريخ مبالغ الدفع
                  </button>
                  
                  {company.Notes && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">ملاحظات: </span>
                        {company.Notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* نموذج تحديد مبلغ الدفع */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <DollarSign size={20} className="text-blue-600" />
                  تحديد مبلغ الدفع
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">الشركة</label>
                  <select
                    value={formData.CompanyID}
                    onChange={(e) => setFormData({...formData, CompanyID: e.target.value})}
                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">اختر الشركة</option>
                    {companies.map(company => (
                      <option key={company.CompanyID} value={company.CompanyID}>
                        {company.CompanyName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">مبلغ الدفع (ريال)</label>
                  <input
                    type="number"
                    value={formData.PaymentAmount}
                    onChange={(e) => setFormData({...formData, PaymentAmount: e.target.value})}
                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="أدخل المبلغ"
                  />
                </div>
                
                <div>
                <label className="block text-sm font-medium mb-1">تاريخ بداية السريان</label>
                <input
                  type="date"
                  value={formData.EffectiveFrom}
                  onChange={(e) => setFormData({...formData, EffectiveFrom: e.target.value})}
                  className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  <strong>هام:</strong> سيتم تطبيق مبلغ الدفع الجديد فقط على المعاملات بدءاً من هذا التاريخ. المعاملات السابقة لن تتأثر.
                </p>
              </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">ملاحظات</label>
                  <textarea
                    value={formData.Notes}
                    onChange={(e) => setFormData({...formData, Notes: e.target.value})}
                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="إضافة ملاحظات (اختياري)"
                  ></textarea>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={saveCompanyPayment}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Save size={16} />
                    حفظ
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* رسائل التنبيه */}
        {toast && (
          <div className={`
            fixed bottom-4 right-4 z-50 
            p-4 rounded-lg shadow-lg 
            flex items-center gap-3
            animate-slide-up
            ${toast.type === 'success' ? 'bg-green-100 text-green-800' : 
              toast.type === 'error' ? 'bg-red-100 text-red-800' : 
              'bg-blue-100 text-blue-800'}
          `}>
            {toast.type === 'success' ? <CheckCircle size={20} /> : 
             toast.type === 'error' ? <AlertCircle size={20} /> : 
             <RefreshCw size={20} className="animate-spin" />}
            {toast.message}
            <button 
              onClick={() => setToast(null)} 
              className="ml-3 hover:opacity-75"
            >
              <X size={16} />
            </button>
          </div>
        )}


{/* نافذة تاريخ إصدارات مبالغ الدفع */}
{showVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileText size={20} className="text-blue-600" />
                تاريخ مبالغ الدفع - {selectedCompanyName}
              </h2>
              <button
                onClick={() => setShowVersionModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            {versionHistory.length === 0 ? (
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">لا توجد إصدارات سابقة متاحة</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">مبلغ الدفع</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">تاريخ بداية السريان</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">تاريخ انتهاء السريان</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">ملاحظات</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">تم بواسطة</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">تاريخ الإنشاء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {versionHistory.map((version, index) => (
                      <tr key={version.VersionID} className={`hover:bg-gray-50 ${index === 0 ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-3 text-sm font-medium">
                          {parseFloat(version.PaymentAmount).toLocaleString()} ريال
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(version.EffectiveFrom).toLocaleDateString('ar-SA')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {version.EffectiveTo ? new Date(version.EffectiveTo).toLocaleDateString('ar-SA') : 'ساري المفعول حاليًا'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {version.Notes || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {version.CreatedBy || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(version.CreatedAt).toLocaleDateString('ar-SA')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowVersionModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}



      </div>

      {/* أضف CSS الإضافي للرسوم المتحركة */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out forwards;
        }
        `
      }} />
    </div>
  );
};

export default Categories;